import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Print registration for the unified Subheader (ES-13522).
 *
 * Lets any page/component that has a dedicated print layout surface a "Print"
 * action on the shared Subheader WITHOUT the Subheader knowing anything about
 * that page. A printable component registers a handler while it is mounted; the
 * Subheader (a sibling rendered under this provider) shows the Print button
 * whenever a handler is registered and invokes it on click.
 *
 * This keeps print behaviour page-owned — e.g. a vehicle comparison tool toggles
 * the vehicle print context before printing, while a safety chart just calls
 * window.print() — and makes adding new printable pages a one-line hook call.
 */
const SubheaderPrintContext = createContext({
  printHandler: null,
  registerPrint: () => () => {},
});

export const SubheaderPrintProvider = ({ children }) => {
  const [printHandler, setPrintHandler] = useState(null);

  const registerPrint = useCallback((fn) => {
    // Store the handler itself; the functional form is only to avoid React
    // treating `fn` as a state updater.
    setPrintHandler(() => fn);
    return () => {
      setPrintHandler((current) => (current === fn ? null : current));
    };
  }, []);

  const value = useMemo(
    () => ({ printHandler, registerPrint }),
    [printHandler, registerPrint],
  );

  return (
    <SubheaderPrintContext.Provider value={value}>
      {children}
    </SubheaderPrintContext.Provider>
  );
};

/** Read the currently registered print handler (used by the Subheader). */
export const useSubheaderPrint = () => useContext(SubheaderPrintContext);

/**
 * Register a Subheader print handler for as long as the calling component is
 * mounted and `enabled`. The latest `handler` is always used, so it may close
 * over changing state/props without forcing a re-registration.
 *
 * @param {() => void} handler  Invoked when the Subheader Print button is used.
 * @param {boolean} [enabled]   Skip registration when false (e.g. mobile).
 */
export const useRegisterSubheaderPrint = (handler, enabled = true) => {
  const { registerPrint } = useContext(SubheaderPrintContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return undefined;
    const stableFn = () => handlerRef.current?.();
    return registerPrint(stableFn);
  }, [enabled, registerPrint]);
};

export default SubheaderPrintContext;
